import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { CheckCircle2, HelpCircle, Info, AlertCircle, User, ChevronDown, Quote, ArrowRight, Plus, X, Moon, Bed, Clock, Coffee, Calendar, Phone, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const SleepAccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-fit">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <Icon className="text-[#8C2F12] w-6 h-6" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50">
              <div className="pt-4">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Soevnproblemer = () => {
  const [openItems, setOpenItems] = useState([]);
  const canonicalUrl = "https://corehypnose.dk/soevnproblemer";

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  return (
    <ServiceLayout 
      title="Søvnproblemer – Sov godt igen og få energien tilbage"
      subtitle="Metakognitiv terapi og hypnose hjælper dig med at slippe tankemylder og overtænkning, så du igen kan sove roligt og vågne udhvilet."
      description={`Måske kender du det: Du har prøvet alle mulige råd og teknikker, men tankerne kører stadig i ring, når du lægger dig. Overtænkning, bekymringer og tankemylder kan holde dig vågen nat efter nat. 

Dagen efter vågner du træt, uden overskud, og med en følelse af ikke at kunne finde hvile, hverken i kroppen eller hovedet. Hos Core Hypnose hjælper jeg dig med at bryde dette mønster.`}
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/9848682d-94ba-4688-b67f-9f8a5a2d153f.png"
      metaDescription="Behandling af søvnproblemer med Metakognitiv Terapi og Hypnose hos Lars Nissen Corell – Psykoterapeut MPF. Mere end 26 års erfaring."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-6 py-3 rounded-md font-bold hover:bg-[#193f10]/90 transition-all shadow-md">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3 rounded-md font-bold hover:bg-[#faf7f2] transition-all">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at finde ro og få søvnen tilbage?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <title>Behandling af søvnproblemer i Valby | Lars Nissen Corell – Psykoterapeut MPF</title>
        <meta name="description" content="Behandling af søvnproblemer med Metakognitiv Terapi og Hypnose hos Lars Nissen Corell – Psykoterapeut MPF. Mere end 26 års erfaring." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Behandling af søvnproblemer i Valby | Lars Nissen Corell – Psykoterapeut MPF" />
        <meta property="og:description" content="Behandling af søvnproblemer med Metakognitiv Terapi og Hypnose hos Lars Nissen Corell – Psykoterapeut MPF. Mere end 26 års erfaring." />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
      </Helmet>

      {/* Testimonial Section */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Jeg frygtede natten, men nu glæder jeg mig til at sove”</h3>
          <div className="text-white/80 mb-6 leading-relaxed space-y-4">
            <p>“I årevis var min seng blevet en kampplads. Så snart jeg lagde hovedet på puden, startede 'filmen' i mit hoved – alt det jeg ikke havde nået, alt det jeg bekymrede mig om. Jeg prøvede alt fra sovepiller til meditation, men intet hjalp rigtigt.”</p>
            <p>“Hos Lars lærte jeg ikke bare at slappe af, men at forstå, at jeg faktisk kan lade tankerne være. Hypnosen gav min krop den ro, den havde glemt, hvordan den fandt selv. Allerede efter anden session begyndte jeg at sove igennem.”</p>
          </div>
          <p className="font-bold">— Mand, 45 år</p>
        </div>
      </div>

      {/* Hvorfor bliver søvnen ved med at drille? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver søvnen ved med at drille?</h2>
        <div className="text-[#3B352F]/70 text-lg leading-relaxed space-y-4">
          <p>
            Søvnbesvær handler sjældent kun om at falde i søvn. Ofte ligger der en indre uro og overaktiv tænkning bag, og jo mere du prøver at tvinge dig selv til ro, jo sværere bliver det. Mange fortæller, at de “lægger sig til at sove, og så starter tankerne”.
          </p>
          <p>
            Behandlingen hos Core Hypnose hjælper dig med at slippe kontrollen og lade søvnen vende naturligt tilbage. Vi arbejder med både tanker, vaner og nervesystem, så du kan få ro om aftenen, og vågne mere frisk og klar.
          </p>
          <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
            Søvnproblemer hos autistiske personer hænger ofte tæt sammen med et overstimuleret nervesystem og sanseindtryk fra dagen, der skal bearbejdes. Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
          </p>
        </div>
      </div>

      {/* NYT FORSKNINGSAFSNIT: Metakognitiv terapi ved søvnløshed */}
      <div className="mb-20 bg-[#FFFDF9] border border-[#E8DDCE] rounded-3xl shadow-sm overflow-hidden">
        {/* Forsknings Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-10 lg:p-12 bg-gradient-to-br from-[#FFFDF9] to-[#F4EBDF] border-b border-[#E8DDCE]">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 text-[#8C2F12] text-xs font-bold uppercase tracking-wider mb-3">
              <span className="w-8 h-[2px] bg-[#8C2F12] inline-block"></span>
              Ny forskning om metakognitiv terapi ved søvnløshed
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#123F13] mb-4 leading-tight">
              Hvad skete der, da personer med søvnløshed fik metakognitiv terapi?
            </h2>
            <p className="text-[#5B554D] text-base sm:text-lg mb-3 leading-relaxed">
              Et klinisk studie fra 2025 fulgte 31 personer med søvnløshed. De kunne få op til 10 sessioner med metakognitiv terapi. Forskerne målte ikke kun søvnen før og efter. De målte også undervejs og fulgte en del af deltagerne efter 3, 6 og 12 måneder.
            </p>
            <p className="text-[#5B554D] text-base sm:text-lg leading-relaxed">
              Her er tallene oversat til almindeligt dansk, så du kan se både, hvad der ser lovende ud, og hvad forskningen endnu ikke kan fortælle os.
            </p>
          </div>
          <div className="lg:col-span-4 self-start bg-white border border-[#E8DDCE] rounded-2xl p-5 shadow-xs">
            <strong className="block text-[#123F13] font-serif text-lg mb-1.5">Forskningskilden</strong>
            <p className="text-xs text-[#71695F] mb-1 font-medium">Salim, Bauducco, Norell &amp; Callesen</p>
            <p className="text-xs text-[#71695F] italic mb-2 leading-snug">
              Metacognitive therapy for insomnia: An open cohort study of clinical outcomes and the role of cognitive attentional syndrome factors in insomnia
            </p>
            <p className="text-xs text-[#71695F] mb-3 leading-snug">
              Sleep Medicine: X, 2025, 10:100153<br />
              DOI: 10.1016/j.sleepx.2025.100153
            </p>
            <a 
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC12513231/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8C2F12] hover:underline"
            >
              Læs forskningsartiklen <ExternalLink size={13} />
            </a>
          </div>
        </div>

        {/* Hurtige fakta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 p-6 sm:px-10 lg:px-12 pt-8 pb-3">
          <div className="bg-[#FAF6F0] border border-[#E8DDCE] rounded-xl p-4 sm:p-5">
            <span className="block font-serif text-[#123F13] text-3xl font-semibold mb-1">31</span>
            <p className="text-xs sm:text-sm text-[#5D574F]">personer startede i studiet.</p>
          </div>
          <div className="bg-[#FAF6F0] border border-[#E8DDCE] rounded-xl p-4 sm:p-5">
            <span className="block font-serif text-[#123F13] text-3xl font-semibold mb-1">30</span>
            <p className="text-xs sm:text-sm text-[#5D574F]">havde en måling lige efter behandlingen.</p>
          </div>
          <div className="bg-[#FAF6F0] border border-[#E8DDCE] rounded-xl p-4 sm:p-5">
            <span className="block font-serif text-[#123F13] text-3xl font-semibold mb-1">op til 10</span>
            <p className="text-xs sm:text-sm text-[#5D574F]">sessioner med metakognitiv terapi indgik i forløbet.</p>
          </div>
          <div className="bg-[#FAF6F0] border border-[#E8DDCE] rounded-xl p-4 sm:p-5">
            <span className="block font-serif text-[#123F13] text-3xl font-semibold mb-1">3, 6, 12 mdr.</span>
            <p className="text-xs sm:text-sm text-[#5D574F]">var tidspunkterne for opfølgning efter behandlingen.</p>
          </div>
        </div>

        {/* Før og efter gennemsnit */}
        <div className="p-6 sm:px-10 lg:px-12 pt-8">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-2">Før og efter: sådan ændrede gennemsnittet sig</h3>
            <p className="text-sm sm:text-base text-[#655F57]">
              Tre mål blev fulgt. ISI handler om søvnløshedens sværhedsgrad. HADS-A måler angstsymptomer, og HADS-D måler depressive symptomer. Et lavere tal er bedre på alle tre mål.
            </p>
          </div>

          <div className="space-y-4">
            {/* ISI */}
            <div className="border border-[#E8DDCE] rounded-2xl bg-white p-5 sm:p-6 shadow-2xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-end mb-4">
                <div className="col-span-2 sm:col-span-1">
                  <strong className="block text-[#123F13] font-semibold text-base sm:text-lg">Søvnløshed, ISI</strong>
                  <span className="text-xs text-[#71695F]">Det vigtigste mål i studiet</span>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Før</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">19,82</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Efter</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">9,80</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Forskel</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl text-[#123f13]">−10,02 point</b>
                </div>
              </div>
              <div className="h-2.5 bg-[#EEE5D9] rounded-full overflow-hidden relative my-3">
                <span className="absolute inset-y-0 left-0 bg-[#D8C7B7] rounded-full w-full"></span>
                <span className="absolute inset-y-0 left-0 bg-[#245C35] rounded-full" style={{ width: '49.4%' }}></span>
              </div>
              <div className="pt-3 border-t border-[#EEE4D9] text-xs sm:text-sm text-[#5D574F] leading-relaxed">
                <strong className="text-[#123F13]">Hvad betyder det?</strong> Gennemsnittet blev omtrent halveret. Studiet brugte en ISI-score på 11 eller højere som klinisk grænse for søvnløshed. Gennemsnittet efter behandlingen lå altså under grænsen.
              </div>
            </div>

            {/* HADS-A */}
            <div className="border border-[#E8DDCE] rounded-2xl bg-white p-5 sm:p-6 shadow-2xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-end mb-4">
                <div className="col-span-2 sm:col-span-1">
                  <strong className="block text-[#123F13] font-semibold text-base sm:text-lg">Angstsymptomer, HADS-A</strong>
                  <span className="text-xs text-[#71695F]">Et ekstra mål ved siden af søvnen</span>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Før</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">9,59</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Efter</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">6,57</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Forskel</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl text-[#123f13]">−3,02 point</b>
                </div>
              </div>
              <div className="h-2.5 bg-[#EEE5D9] rounded-full overflow-hidden relative my-3">
                <span className="absolute inset-y-0 left-0 bg-[#D8C7B7] rounded-full w-full"></span>
                <span className="absolute inset-y-0 left-0 bg-[#245C35] rounded-full" style={{ width: '68.5%' }}></span>
              </div>
              <div className="pt-3 border-t border-[#EEE4D9] text-xs sm:text-sm text-[#5D574F] leading-relaxed">
                <strong className="text-[#123F13]">Hvad betyder det?</strong> Angstsymptomerne faldt også i gennemsnit. Studiet var lavet for at undersøge søvnløshed, så tallet er et interessant sidefund og ikke bevis for, at behandlingen i sig selv behandler alle former for angst.
              </div>
            </div>

            {/* HADS-D */}
            <div className="border border-[#E8DDCE] rounded-2xl bg-white p-5 sm:p-6 shadow-2xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-end mb-4">
                <div className="col-span-2 sm:col-span-1">
                  <strong className="block text-[#123F13] font-semibold text-base sm:text-lg">Depressive symptomer, HADS-D</strong>
                  <span className="text-xs text-[#71695F]">Et ekstra mål ved siden af søvnen</span>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Før</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">5,09</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Efter</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl">3,14</b>
                </div>
                <div>
                  <small className="text-xs text-[#71695F] block">Forskel</small>
                  <b className="font-serif text-[#123F13] text-xl sm:text-2xl text-[#123f13]">−1,95 point</b>
                </div>
              </div>
              <div className="h-2.5 bg-[#EEE5D9] rounded-full overflow-hidden relative my-3">
                <span className="absolute inset-y-0 left-0 bg-[#D8C7B7] rounded-full w-full"></span>
                <span className="absolute inset-y-0 left-0 bg-[#245C35] rounded-full" style={{ width: '61.7%' }}></span>
              </div>
              <div className="pt-3 border-t border-[#EEE4D9] text-xs sm:text-sm text-[#5D574F] leading-relaxed">
                <strong className="text-[#123F13]">Hvad betyder det?</strong> Også her faldt gennemsnittet. Resultatet fortæller, at søvnforbedringen skete samtidig med færre depressive symptomer i gruppen. Det fortæller ikke, hvad der var årsag til hvad.
              </div>
            </div>
          </div>
        </div>

        {/* Hvor mange fik en mærkbar forbedring? */}
        <div className="p-6 sm:px-10 lg:px-12 pt-10">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-2">Hvor mange fik en mærkbar forbedring?</h3>
            <p className="text-sm sm:text-base text-[#655F57]">
              Forskerne brugte flere grænser. Det er nyttigt, fordi et gennemsnit kan skjule store forskelle fra person til person.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            <div className="border border-[#E8DDCE] rounded-xl p-5 bg-[#FAF6F0]">
              <div className="font-serif text-[#123F13] text-3xl font-semibold mb-1">77 %</div>
              <strong className="block text-[#123F13] text-sm font-semibold mb-1.5">23 ud af 30</strong>
              <p className="text-xs sm:text-sm text-[#625C54]">nåede mindst en lille forbedring. I studiet betød det et fald på mindst 4,65 point på ISI.</p>
            </div>
            <div className="border border-[#E8DDCE] rounded-xl p-5 bg-[#FAF6F0]">
              <div className="font-serif text-[#123F13] text-3xl font-semibold mb-1">60 %</div>
              <strong className="block text-[#123F13] text-sm font-semibold mb-1.5">18 ud af 30</strong>
              <p className="text-xs sm:text-sm text-[#625C54]">nåede mindst en moderat forbedring. Her var grænsen et fald på mindst 8,36 ISI-point.</p>
            </div>
            <div className="border border-[#E8DDCE] rounded-xl p-5 bg-[#FAF6F0]">
              <div className="font-serif text-[#123F13] text-3xl font-semibold mb-1">67 %</div>
              <strong className="block text-[#123F13] text-sm font-semibold mb-1.5">20 ud af 30</strong>
              <p className="text-xs sm:text-sm text-[#625C54]">lå efter behandlingen under studiets kliniske grænse for søvnløshed, altså under ISI 11.</p>
            </div>
            <div className="border border-[#E8DDCE] rounded-xl p-5 bg-[#FAF6F0]">
              <div className="font-serif text-[#123F13] text-3xl font-semibold mb-1">57 %</div>
              <strong className="block text-[#123F13] text-sm font-semibold mb-1.5">17 ud af 30</strong>
              <p className="text-xs sm:text-sm text-[#625C54]">opfyldte det strengere remissionsmål: en tydelig og pålidelig forbedring kombineret med et niveau under den kliniske grænse.</p>
            </div>
          </div>

          <div className="mt-4 border-l-3 border-[#8C2F12] p-3.5 sm:p-4 bg-[#FFF8F3] rounded-r-xl text-xs sm:text-sm text-[#675F56] leading-relaxed">
            <strong className="text-[#8C2F12]">Vigtigt:</strong> Procenterne kan ikke lægges sammen. Den samme person kan godt være med i flere grupper. En person kan for eksempel både have en moderat forbedring og samtidig ende under den kliniske grænse.
          </div>
        </div>

        {/* Holdt forbedringen sig efter behandlingen? */}
        <div className="p-6 sm:px-10 lg:px-12 pt-10">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-2">Holdt forbedringen sig efter behandlingen?</h3>
            <p className="text-sm sm:text-base text-[#655F57]">
              24 af de 31 deltagere svarede på mindst én opfølgning. Derfor skal opfølgningstallene læses mere forsigtigt end målingen lige efter behandlingen.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Timeline */}
            <div className="lg:col-span-7 grid grid-cols-2 gap-3.5 sm:gap-4">
              <div className="border border-[#E8DDCE] rounded-xl bg-white p-4 sm:p-5 flex flex-col items-center justify-center text-center shadow-2xs">
                <span className="block text-[#8C2F12] text-[11px] font-bold uppercase tracking-wider mb-1">Efter behandling</span>
                <b className="block font-serif text-[#123F13] text-2xl sm:text-3xl mb-1">9,80</b>
                <small className="text-xs text-[#71695F]">gennemsnitlig ISI</small>
              </div>
              <div className="border border-[#E8DDCE] rounded-xl bg-white p-4 sm:p-5 flex flex-col items-center justify-center text-center shadow-2xs">
                <span className="block text-[#8C2F12] text-[11px] font-bold uppercase tracking-wider mb-1">3 måneder</span>
                <b className="block font-serif text-[#123F13] text-2xl sm:text-3xl mb-1">7,32</b>
                <small className="text-xs text-[#71695F]">gennemsnitlig ISI</small>
              </div>
              <div className="border border-[#E8DDCE] rounded-xl bg-white p-4 sm:p-5 flex flex-col items-center justify-center text-center shadow-2xs">
                <span className="block text-[#8C2F12] text-[11px] font-bold uppercase tracking-wider mb-1">6 måneder</span>
                <b className="block font-serif text-[#123F13] text-2xl sm:text-3xl mb-1">9,62</b>
                <small className="text-xs text-[#71695F]">gennemsnitlig ISI</small>
              </div>
              <div className="border border-[#E8DDCE] rounded-xl bg-white p-4 sm:p-5 flex flex-col items-center justify-center text-center shadow-2xs">
                <span className="block text-[#8C2F12] text-[11px] font-bold uppercase tracking-wider mb-1">12 måneder</span>
                <b className="block font-serif text-[#123F13] text-2xl sm:text-3xl mb-1">9,08</b>
                <small className="text-xs text-[#71695F]">gennemsnitlig ISI</small>
              </div>
            </div>

            {/* Aside opfølgning */}
            <div className="lg:col-span-5 bg-[#123F13] text-[#F7F3EB] rounded-2xl p-5 sm:p-6 shadow-sm">
              <h4 className="text-white font-serif text-lg mb-1.5">Seneste svar fra de 24</h4>
              <p className="text-xs text-[#E8EFE9] mb-4 leading-relaxed">
                Forskerne sammenlignede den seneste tilgængelige opfølgning med målingen lige efter behandling.
              </p>
              <div className="space-y-2">
                <div className="bg-white/10 border border-white/15 rounded-lg p-2.5 text-xs">
                  <b className="text-white block mb-0.5">16,7 % · 4 personer</b>
                  <span>fik yderligere forbedring.</span>
                </div>
                <div className="bg-white/10 border border-white/15 rounded-lg p-2.5 text-xs">
                  <b className="text-white block mb-0.5">70,8 % · 17 personer</b>
                  <span>havde ingen stor ændring, hvilket peger på stabilitet eller bevaret gevinst.</span>
                </div>
                <div className="bg-white/10 border border-white/15 rounded-lg p-2.5 text-xs">
                  <b className="text-white block mb-0.5">12,5 % · 3 personer</b>
                  <span>havde en lille forværring.</span>
                </div>
                <div className="bg-white/10 border border-white/15 rounded-lg p-2.5 text-xs">
                  <b className="text-white block mb-0.5">54 % · 13 personer</b>
                  <span>lå ved seneste opfølgning under den kliniske ISI-grænse.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hvad så forskerne på bag søvnproblemerne? (CAS) */}
        <div className="p-6 sm:px-10 lg:px-12 pt-10">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-2">Hvad så forskerne på bag søvnproblemerne?</h3>
            <p className="text-sm sm:text-base text-[#655F57]">
              Et centralt spor var det, forskerne kalder kognitivt opmærksomhedssyndrom, ofte forkortet CAS. Navnet lyder teknisk. Indholdet er mere hverdagsagtigt.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 bg-[#F3EADF]/70 border border-[#E8DDCE] rounded-2xl p-5 sm:p-6">
            <div className="lg:col-span-5 bg-white border border-[#E8DDCE] rounded-xl p-5 shadow-2xs">
              <h4 className="text-[#123F13] font-serif text-lg mb-2">CAS på almindeligt dansk</h4>
              <p className="text-xs sm:text-sm text-[#5B554D] mb-3">Det handler blandt andet om, hvor meget man:</p>
              <ul className="space-y-2 text-xs sm:text-sm text-[#5B554D]">
                <li className="flex items-start gap-2">
                  <span className="text-[#8C2F12] font-bold text-base leading-none mt-0.5">•</span>
                  <span>bekymrer sig om søvnen og næste dag</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8C2F12] font-bold text-base leading-none mt-0.5">•</span>
                  <span>grubler over, hvorfor man ikke sover</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8C2F12] font-bold text-base leading-none mt-0.5">•</span>
                  <span>holder øje med tegn på, at noget er galt</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8C2F12] font-bold text-base leading-none mt-0.5">•</span>
                  <span>bruger mentale strategier for at få kontrol over tanker eller søvn</span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                <h4 className="text-[#123F13] font-serif text-lg mb-2">Hvad fandt studiet?</h4>
                <p className="text-xs sm:text-sm text-[#5E574F] leading-relaxed mb-3">
                  Højere brug af CAS-strategier hang sammen med højere sværhedsgrad af søvnløshed over tid. Når brugen af strategier ændrede sig hos den enkelte, hang det også sammen med ændringer i søvnproblemernes sværhedsgrad.
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E8DDCE] shadow-2xs text-xs sm:text-sm text-[#5D574F] leading-relaxed">
                <strong className="text-[#123F13]">Oversat til hverdagen:</strong> Jo mere søvnen bliver noget, man overvåger, analyserer og prøver at styre mentalt, jo mere kan søvnen komme til at fylde. Sammenhængen er vigtig, men et observationsfund kan ikke alene bevise årsag.
              </div>
            </div>
          </div>
        </div>

        {/* Hvis du vil forstå tallene lidt bedre (Glossary) */}
        <div className="p-6 sm:px-10 lg:px-12 pt-10">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-2">Hvis du vil forstå tallene lidt bedre</h3>
            <p className="text-sm sm:text-base text-[#655F57]">
              Forskningsartikler bruger ord, som let kan lyde mere sikre, end de er. Her er en jordnær oversættelse.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Kort 1 */}
            <details className="bg-white border border-[#E8DDCE] rounded-xl px-4 py-3 group cursor-pointer transition-colors hover:border-[#123F13]/30 flex flex-col justify-start" open>
              <summary className="font-serif font-medium text-[#123F13] text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden flex items-center justify-between select-none">
                <span>Hvad betyder effektstørrelse?</span>
                <span className="text-[#8C2F12] text-xl font-medium group-open:rotate-45 transition-transform duration-200 shrink-0 ml-3 inline-block leading-none">+</span>
              </summary>
              <p className="text-xs sm:text-sm text-[#625B53] pt-2.5 leading-relaxed border-t border-[#E8DDCE]/50 mt-2.5">
                Effektstørrelse siger noget om, hvor stor ændringen var i forhold til variationen i gruppen. For søvnløshed var Hedges' g 1,64, hvilket er en stor statistisk ændring. Angst lå på 0,76 og depressive symptomer på 0,65. En stor effektstørrelse fortæller ikke i sig selv, at behandlingen var hele årsagen.
              </p>
            </details>

            {/* Kort 2 */}
            <details className="bg-white border border-[#E8DDCE] rounded-xl px-4 py-3 group cursor-pointer transition-colors hover:border-[#123F13]/30 flex flex-col justify-start" open>
              <summary className="font-serif font-medium text-[#123F13] text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden flex items-center justify-between select-none">
                <span>Hvad betyder p-værdi?</span>
                <span className="text-[#8C2F12] text-xl font-medium group-open:rotate-45 transition-transform duration-200 shrink-0 ml-3 inline-block leading-none">+</span>
              </summary>
              <p className="text-xs sm:text-sm text-[#625B53] pt-2.5 leading-relaxed border-t border-[#E8DDCE]/50 mt-2.5">
                ISI havde p&lt;0,001. Angst og depressive symptomer havde p&lt;0,01. Groft sagt viser værdierne, at de målte forskelle er svære at forklare som tilfældige udsving, hvis der slet ingen reel ændring var. P-værdien fortæller ikke, hvor vigtig ændringen er for den enkelte, og den beviser ikke årsag.
              </p>
            </details>

            {/* Kort 3 */}
            <details className="bg-white border border-[#E8DDCE] rounded-xl px-4 py-3 group cursor-pointer transition-colors hover:border-[#123F13]/30 flex flex-col justify-start">
              <summary className="font-serif font-medium text-[#123F13] text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden flex items-center justify-between select-none">
                <span>Hvad betyder remission?</span>
                <span className="text-[#8C2F12] text-xl font-medium group-open:rotate-45 transition-transform duration-200 shrink-0 ml-3 inline-block leading-none">+</span>
              </summary>
              <p className="text-xs sm:text-sm text-[#625B53] pt-2.5 leading-relaxed border-t border-[#E8DDCE]/50 mt-2.5">
                Forskerne brugte et strengt mål. Deltageren skulle både have en pålidelig forbedring og ende under den kliniske grænse for søvnløshed. Derfor er 57 % remission lavere end de 67 %, som lå under grænsen efter behandlingen.
              </p>
            </details>

            {/* Kort 4 */}
            <details className="bg-white border border-[#E8DDCE] rounded-xl px-4 py-3 group cursor-pointer transition-colors hover:border-[#123F13]/30 flex flex-col justify-start">
              <summary className="font-serif font-medium text-[#123F13] text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden flex items-center justify-between select-none">
                <span>Hvorfor er 30 vigtigere end 31 ved eftermålingen?</span>
                <span className="text-[#8C2F12] text-xl font-medium group-open:rotate-45 transition-transform duration-200 shrink-0 ml-3 inline-block leading-none">+</span>
              </summary>
              <p className="text-xs sm:text-sm text-[#625B53] pt-2.5 leading-relaxed border-t border-[#E8DDCE]/50 mt-2.5">
                31 startede, men én manglede målingen lige efter behandlingen. Derfor bygger de vigtigste før-efter-tal på 30 personer. Ved senere opfølgning var der færre svar, så usikkerheden bliver større.
              </p>
            </details>
          </div>
        </div>

        {/* Lovende forskning forbehold */}
        <div className="p-6 sm:px-10 lg:px-12 pt-8">
          <div className="flex items-start gap-4 border border-[#DBCDFF]/20 bg-white border-[#DBCDBF] rounded-2xl p-5 shadow-2xs">
            <div className="w-9 h-9 rounded-full bg-[#F2E3DA] text-[#8C2F12] flex items-center justify-center font-serif font-bold text-base shrink-0">
              i
            </div>
            <div>
              <strong className="block text-[#123F13] font-serif text-base mb-1">Lovende forskning, men ikke et endeligt bevis</strong>
              <p className="text-xs sm:text-sm text-[#645D55] leading-relaxed">
                Studiet var lille og havde ingen kontrolgruppe. Søvnen blev målt med spørgeskemaer og ikke med objektive søvnmålinger som aktigrafi eller polysomnografi. Forskerne sammenlignede heller ikke direkte med CBT-I. Derfor kan man ikke konkludere, at metakognitiv terapi alene skabte hele forbedringen, eller at metoden er bedre end andre behandlinger. Forfatterne anbefaler større randomiserede kontrollerede studier.
              </p>
            </div>
          </div>
        </div>

        {/* Hvad kan du som klient tage med dig? */}
        <div className="p-6 sm:px-10 lg:px-12 pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5 bg-[#123F13] text-[#F7F3EB] rounded-2xl p-6 flex flex-col justify-start">
              <h3 className="font-serif text-xl sm:text-2xl text-white mb-3">Hvad kan du som klient tage med dig?</h3>
              <p className="text-xs sm:text-sm text-[#E8EFE9] leading-relaxed">
                Forskningen giver ikke en garanti. Den peger på en anden måde at forstå søvnproblemer på: Måske behøver løsningen ikke være mere kontrol over søvnen, men mindre mental kamp med alt det, der dukker op omkring den.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-2.5">
              <div className="flex items-start gap-3.5 bg-white border border-[#E8DDCE] rounded-xl p-4 shadow-2xs">
                <div className="w-8 h-8 rounded-full border border-[#CA8A70] text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">
                  1
                </div>
                <div>
                  <strong className="block text-[#123F13] text-sm mb-0.5">Tanker ved sengetid behøver ikke løses</strong>
                  <p className="text-xs text-[#625C54]">En tanke om morgendagen må gerne være der uden at blive analyseret færdig klokken to om natten.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 bg-white border border-[#E8DDCE] rounded-xl p-4 shadow-2xs">
                <div className="w-8 h-8 rounded-full border border-[#CA8A70] text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">
                  2
                </div>
                <div>
                  <strong className="block text-[#123F13] text-sm mb-0.5">Overvågning kan holde søvnen i centrum</strong>
                  <p className="text-xs text-[#625C54]">Klokken, kroppen og spørgsmålet “sover jeg snart?” kan blive en opgave, som holder opmærksomheden vågen.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 bg-white border border-[#E8DDCE] rounded-xl p-4 shadow-2xs">
                <div className="w-8 h-8 rounded-full border border-[#CA8A70] text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">
                  3
                </div>
                <div>
                  <strong className="block text-[#123F13] text-sm mb-0.5">Mindre kontrol er ikke det samme som at give op</strong>
                  <p className="text-xs text-[#625C54]">Det handler om at bruge mindre energi på at styre tanker og søvn og mere på at lade søvnen passe sin egen proces.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 bg-white border border-[#E8DDCE] rounded-xl p-4 shadow-2xs">
                <div className="w-8 h-8 rounded-full border border-[#CA8A70] text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">
                  4
                </div>
                <div>
                  <strong className="block text-[#123F13] text-sm mb-0.5">Forandring kan måles på mere end antal sovetimer</strong>
                  <p className="text-xs text-[#625C54]">Mindre bekymring, mindre grubleri og mindre kontrolarbejde kan være vigtige tegn på, at forholdet til søvnen er ved at ændre sig.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note om behandling hos Core Hypnose */}
        <div className="mx-6 sm:mx-10 lg:mx-12 mt-6 bg-[#F9F2E9] border border-[#E8DDCE] rounded-2xl p-4 sm:p-5">
          <strong className="block text-[#123F13] font-serif text-sm sm:text-base mb-1">Om behandlingen hos Core Hypnose</strong>
          <p className="text-xs sm:text-sm text-[#625B53] leading-relaxed">
            Forskningsstudiet undersøgte metakognitiv terapi. Det undersøgte ikke hypnose. I klinikken arbejder jeg med metakognitiv terapi og hypnose, når det giver faglig mening. Forskningsresultaterne ovenfor gælder derfor den metakognitive del og skal ikke læses som dokumentation for hypnose.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-8 border-t border-[#E8DDCE] p-6 sm:px-10 lg:px-12 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-gradient-to-r from-[#FAF6F0] to-[#FFFDF9]">
          <div>
            <h3 className="text-xl sm:text-2xl font-serif text-[#123F13] mb-1.5">Vil du undersøge, hvad der holder din søvn fast?</h3>
            <p className="text-xs sm:text-sm text-[#675F57] max-w-2xl leading-relaxed">
              På første behandlingssession ser vi konkret på, hvad der sker hos dig før sengetid, om natten og dagen efter. Målet er at finde ud af, hvilke processer der fylder mest, og hvad der giver mening at arbejde videre med.
            </p>
          </div>
          <Link 
            to="/bestil-tid"
            className="inline-flex items-center justify-center whitespace-nowrap bg-[#8C2F12] text-white px-6 py-3.5 rounded-lg font-bold hover:bg-[#a33a1a] transition-all shadow-md shrink-0 text-sm"
          >
            Book første behandlingssession
          </Link>
        </div>
      </div>

      {/* Jeg arbejder anderledes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
        <div>
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Sådan hjælper jeg dig</h2>
          <p className="text-[#3B352F]/70 mb-6">
            Jeg kombinerer metakognitiv terapi med hypnose for at ramme både de bevidste tankemønstre og den ubevidste uro i kroppen.
          </p>
          <div className="space-y-4">
            {[
              { title: "Forstå dine søvnmønstre", desc: "Vi kortlægger, hvad der fastholder dine søvnproblemer." },
              { title: "Metakognitive teknikker", desc: "Lær at slippe overtænkning og bekymringer." },
              { title: "Hypnose", desc: "Skab dyb ro i kroppen og styrk evnen til at give slip." },
              { title: "Hjemmeopgaver", desc: "Konkrete redskaber til at skabe nye, sunde søvnvaner." }
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-[#8C2F12] mt-1 shrink-0" />
                <p className="text-[#3B352F]/80">
                  <strong className="text-[#123F13]">{item.title}:</strong> {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#F4EFE7] p-8 rounded-2xl border border-[#E8DDCE] flex flex-col justify-center items-center text-center">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">Søvnløshed behøver ikke være din hverdag</h3>
          <p className="text-[#3B352F]/70">
            Lad mig vise dig, hvordan du kan genvinde kontrollen over din nattesøvn og få dit overskud tilbage.
          </p>
          <Link 
            to="/bestil-tid" 
            className="inline-flex items-center gap-2 mt-6 bg-[#8C2F12] text-white px-6 py-3 rounded-full font-medium hover:bg-[#8C2F12]/90 transition-colors"
          >
            Book en session
          </Link>
        </div>
      </div>

      {/* Symptomer */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Symptomer på søvnproblemer</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {/* Ved sengetid */}
          <SleepAccordionItem 
            title="Ved sengetid" 
            icon={Moon}
            isOpen={openItems.includes('night')}
            onClick={() => toggleItem('night')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Tankemylder og uro</strong> – tankerne kører i ring, så snart du lægger dig.</li>
              <li>• <strong>Svært ved at falde i søvn</strong> – du ligger vågen i timevis.</li>
              <li>• <strong>Frygt for natten</strong> – du bekymrer dig allerede om dagen for, om du kan sove.</li>
              <li>• <strong>Rastløse ben eller kropslig uro</strong> – svært ved at finde en behagelig stilling.</li>
            </ul>
          </SleepAccordionItem>

          {/* I løbet af natten */}
          <SleepAccordionItem 
            title="I løbet af natten" 
            icon={Bed}
            isOpen={openItems.includes('during')}
            onClick={() => toggleItem('during')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Hyppige opvågninger</strong> – du vågner mange gange uden grund.</li>
              <li>• <strong>Svært ved at falde i søvn igen</strong> – når du vågner, starter tankerne med det samme.</li>
              <li>• <strong>Tidlig morgenvågning</strong> – du vågner længe før vækkeuret og kan ikke sove igen.</li>
              <li>• <strong>Let søvn</strong> – du føler aldrig, at du kommer ned i den dybe, restituerende søvn.</li>
            </ul>
          </SleepAccordionItem>

          {/* I dagtimerne */}
          <SleepAccordionItem 
            title="I dagtimerne" 
            icon={Clock}
            isOpen={openItems.includes('day')}
            onClick={() => toggleItem('day')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Konstant træthed</strong> – en følelse af at være udmattet hele dagen.</li>
              <li>• <strong>Koncentrationsbesvær</strong> – svært ved at fokusere på arbejde eller samtaler.</li>
              <li>• <strong>Hukommelsesbesvær</strong> – du glemmer småting og føler dig "tåget".</li>
              <li>• <strong>Lav motivation</strong> – svært ved at komme i gang med dagens opgaver.</li>
            </ul>
          </SleepAccordionItem>

          {/* Følelsesmæssigt */}
          <SleepAccordionItem 
            title="Følelsesmæssigt" 
            icon={AlertCircle}
            isOpen={openItems.includes('emotional')}
            onClick={() => toggleItem('emotional')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Irritabilitet</strong> – du har en kortere lunte over for dine omgivelser.</li>
              <li>• <strong>Humørsvingninger</strong> – følelserne sidder uden på tøjet.</li>
              <li>• <strong>Øget angst eller stress</strong> – mangel på søvn gør dig mere sårbar over for pres.</li>
              <li>• <strong>Oplevelse af at være “i hovedet”</strong> – svært ved at være til stede i nuet.</li>
            </ul>
          </SleepAccordionItem>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10">Ofte stillede spørgsmål om søvnbehandling</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              q: "Hvor mange sessioner har jeg brug for?",
              a: "De fleste mærker en markant forskel efter 3-5 sessioner. Nogle har brug for lidt mere, hvis søvnproblemerne er tæt forbundet med svær angst eller stress."
            },
            {
              q: "Kan jeg bruge behandlingen sammen med sovemedicin?",
              a: "Ja, behandlingen kan sagtens køre sideløbende. Målet er ofte, at du i samråd med din læge kan trappe ud af medicinen, når de naturlige søvnmønstre vender tilbage."
            },
            {
              q: "Hvordan foregår hypnosen?",
              a: "Hypnosen er en behagelig tilstand af dyb afslapning, hvor vi arbejder med at berolige dit nervesystem og ændre de ubevidste mønstre, der holder dig vågen."
            },
            {
              q: "Virker det også mod tankemylder?",
              a: "Ja, i høj grad. Gennem metakognitiv terapi lærer du konkrete teknikker til at lade tankerne passere uden at gå ind i dem, hvilket er nøglen til at falde i søvn."
            }
          ].map((faq, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-[#E8DDCE]">
              <h3 className="text-lg font-bold text-[#123F13] mb-3 flex items-center gap-2">
                <HelpCircle className="text-[#8C2F12] shrink-0" size={20} />
                {faq.q}
              </h3>
              <p className="text-[#3B352F]/70">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Soevnproblemer;
